import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { InteractiveMessage } from "@/components/interactive-message";
import { Message, MessageContent } from "@/components/message";
import type { InteractiveSpec } from "@/types";

export const Route = createFileRoute("/interactive-demo")({
  component: InteractiveDemoPage,
});

// --- 1. Mood Check-in ---
const moodCheck: InteractiveSpec = {
  root: "mood-card",
  elements: {
    "mood-card": {
      type: "Card",
      props: {
        title: "How are you feeling right now?",
        description: "Pick the closest one \u2014 no wrong answers.",
      },
      children: ["mood-buttons"],
    },
    "mood-buttons": {
      type: "ButtonGroup",
      props: { direction: "horizontal" },
      children: ["btn-great", "btn-okay", "btn-meh", "btn-bad"],
    },
    "btn-great": {
      type: "Button",
      props: { label: "\ud83d\ude0a Great", variant: "outline", size: "sm" },
      on: {
        press: {
          action: "auto_send",
          params: { text: "I'm feeling great today!" },
        },
      },
    },
    "btn-okay": {
      type: "Button",
      props: { label: "\ud83d\ude42 Okay", variant: "outline", size: "sm" },
      on: {
        press: {
          action: "auto_send",
          params: { text: "I'm doing okay, nothing major." },
        },
      },
    },
    "btn-meh": {
      type: "Button",
      props: { label: "\ud83d\ude15 Meh", variant: "outline", size: "sm" },
      on: {
        press: {
          action: "auto_send",
          params: { text: "I'm feeling kind of meh right now." },
        },
      },
    },
    "btn-bad": {
      type: "Button",
      props: { label: "\ud83d\ude1e Not good", variant: "outline", size: "sm" },
      on: {
        press: {
          action: "auto_send",
          params: { text: "I'm not doing so great right now." },
        },
      },
    },
  },
};

// --- 2. Coping Strategy Picker ---
const copingToolkit: InteractiveSpec = {
  root: "coping-card",
  elements: {
    "coping-card": {
      type: "Card",
      props: {
        title: "What kind of coping sounds good right now?",
        description: "Pick a vibe and I'll share some ideas.",
      },
      children: ["coping-buttons"],
    },
    "coping-buttons": {
      type: "ButtonGroup",
      props: { direction: "horizontal" },
      children: ["btn-body", "btn-creative", "btn-calm", "btn-talk"],
    },
    "btn-body": {
      type: "Button",
      props: {
        label: "\ud83c\udfc3 Move my body",
        variant: "outline",
        size: "sm",
      },
      on: {
        press: {
          action: "auto_send",
          params: { text: "I'd like some physical coping strategies." },
        },
      },
    },
    "btn-creative": {
      type: "Button",
      props: {
        label: "\ud83c\udfa8 Get creative",
        variant: "outline",
        size: "sm",
      },
      on: {
        press: {
          action: "auto_send",
          params: { text: "Show me some creative coping strategies." },
        },
      },
    },
    "btn-calm": {
      type: "Button",
      props: {
        label: "\ud83e\uddd8 Calm my mind",
        variant: "outline",
        size: "sm",
      },
      on: {
        press: {
          action: "auto_send",
          params: {
            text: "I want some mindfulness or calming techniques.",
          },
        },
      },
    },
    "btn-talk": {
      type: "Button",
      props: {
        label: "\ud83d\udcac Talk it out",
        variant: "outline",
        size: "sm",
      },
      on: {
        press: {
          action: "auto_send",
          params: { text: "I'd like to talk through what I'm feeling." },
        },
      },
    },
  },
};

// --- 3. Breathing Exercise ---
const breathing: InteractiveSpec = {
  root: "breathing-card",
  elements: {
    "breathing-card": {
      type: "Card",
      props: {
        title: "Let's try box breathing",
        description:
          "A quick way to calm your nervous system. Try 3\u20134 rounds.",
      },
      children: ["steps", "note", "breathing-buttons"],
    },
    steps: {
      type: "List",
      props: {
        items: [
          "Breathe IN slowly for 4 seconds",
          "HOLD your breath for 4 seconds",
          "Breathe OUT slowly for 4 seconds",
          "HOLD again for 4 seconds",
        ],
        ordered: true,
      },
    },
    note: {
      type: "Text",
      props: {
        content:
          "Repeat 3\u20134 times. It\u2019s okay if it feels weird at first \u2014 that\u2019s totally normal.",
      },
    },
    "breathing-buttons": {
      type: "ButtonGroup",
      props: { direction: "horizontal" },
      children: ["btn-tried", "btn-guide"],
    },
    "btn-tried": {
      type: "Button",
      props: { label: "\u2705 I tried it", variant: "outline", size: "sm" },
      on: {
        press: {
          action: "auto_send",
          params: { text: "I just tried the breathing exercise." },
        },
      },
    },
    "btn-guide": {
      type: "Button",
      props: {
        label: "\ud83d\udd04 Guide me",
        variant: "secondary",
        size: "sm",
      },
      on: {
        press: {
          action: "auto_send",
          params: {
            text: "Can you walk me through the breathing step by step?",
          },
        },
      },
    },
  },
};

// --- 4. Ice Breaker ---
const iceBreaker: InteractiveSpec = {
  root: "ice-card",
  elements: {
    "ice-card": {
      type: "Card",
      props: { title: "Quick round \u2014 this or that?", description: null },
      children: ["ice-question", "ice-buttons"],
    },
    "ice-question": { type: "Text", props: { content: "Dogs or cats?" } },
    "ice-buttons": {
      type: "ButtonGroup",
      props: { direction: "horizontal" },
      children: ["btn-a", "btn-b"],
    },
    "btn-a": {
      type: "Button",
      props: { label: "\ud83d\udc36 Dogs", variant: "outline", size: "sm" },
      on: { press: { action: "auto_send", params: { text: "Dogs, 100%!" } } },
    },
    "btn-b": {
      type: "Button",
      props: { label: "\ud83d\udc31 Cats", variant: "outline", size: "sm" },
      on: {
        press: { action: "auto_send", params: { text: "Cats all the way!" } },
      },
    },
  },
};

// --- 5. Session Check-in ---
const checkIn: InteractiveSpec = {
  root: "checkin-card",
  elements: {
    "checkin-card": {
      type: "Card",
      props: {
        title: "Quick check-in",
        description: "How's this chat going so far?",
      },
      children: ["checkin-buttons"],
    },
    "checkin-buttons": {
      type: "ButtonGroup",
      props: { direction: "horizontal" },
      children: ["btn-helpful", "btn-okay", "btn-nah"],
    },
    "btn-helpful": {
      type: "Button",
      props: { label: "\ud83d\udc4d Helpful", variant: "outline", size: "sm" },
      on: {
        press: {
          action: "auto_send",
          params: { text: "This has been really helpful, thanks!" },
        },
      },
    },
    "btn-okay": {
      type: "Button",
      props: {
        label: "\ud83d\ude10 It's okay",
        variant: "outline",
        size: "sm",
      },
      on: {
        press: { action: "auto_send", params: { text: "It's okay so far." } },
      },
    },
    "btn-nah": {
      type: "Button",
      props: {
        label: "\ud83d\udc4e Not really",
        variant: "outline",
        size: "sm",
      },
      on: {
        press: {
          action: "auto_send",
          params: { text: "Honestly, this isn't really helping." },
        },
      },
    },
  },
};

// --- 6. Motivational Boost ---
const motivation: InteractiveSpec = {
  root: "motivation-card",
  elements: {
    "motivation-card": {
      type: "Card",
      props: { title: "A little boost for you", description: null },
      children: [
        "hero-emoji",
        "quote-text",
        "attribution",
        "sep",
        "motivation-buttons",
      ],
    },
    "hero-emoji": { type: "Emoji", props: { emoji: "\u2728", size: "lg" } },
    "quote-text": {
      type: "Text",
      props: {
        content:
          "\u201cYou are braver than you believe, stronger than you seem, and smarter than you think.\u201d",
      },
    },
    attribution: { type: "Text", props: { content: "\u2014 A.A. Milne" } },
    sep: { type: "Separator", props: {} },
    "motivation-buttons": {
      type: "ButtonGroup",
      props: { direction: "horizontal" },
      children: ["btn-love", "btn-another"],
    },
    "btn-love": {
      type: "Button",
      props: { label: "\u2764\ufe0f Love it", variant: "outline", size: "sm" },
      on: {
        press: {
          action: "auto_send",
          params: { text: "I love that quote! It really resonates." },
        },
      },
    },
    "btn-another": {
      type: "Button",
      props: {
        label: "\ud83d\udd04 Show me another",
        variant: "secondary",
        size: "sm",
      },
      on: {
        press: {
          action: "auto_send",
          params: { text: "Can you show me another motivational quote?" },
        },
      },
    },
  },
};

// --- 7. Session Feedback ---
const feedback: InteractiveSpec = {
  root: "feedback-card",
  elements: {
    "feedback-card": {
      type: "Card",
      props: {
        title: "Rate this conversation",
        description:
          "Your feedback helps me improve. How would you rate our chat?",
      },
      children: ["rating-row", "sep", "feedback-input"],
    },
    "rating-row": {
      type: "ButtonGroup",
      props: { direction: "horizontal" },
      children: ["rate-1", "rate-2", "rate-3", "rate-4", "rate-5"],
    },
    "rate-1": {
      type: "Button",
      props: { label: "1 \u2b50", variant: "outline", size: "sm" },
      on: {
        press: {
          action: "auto_send",
          params: { text: "I rate this conversation 1 out of 5 stars." },
        },
      },
    },
    "rate-2": {
      type: "Button",
      props: { label: "2 \u2b50", variant: "outline", size: "sm" },
      on: {
        press: {
          action: "auto_send",
          params: { text: "I rate this conversation 2 out of 5 stars." },
        },
      },
    },
    "rate-3": {
      type: "Button",
      props: { label: "3 \u2b50", variant: "outline", size: "sm" },
      on: {
        press: {
          action: "auto_send",
          params: { text: "I rate this conversation 3 out of 5 stars." },
        },
      },
    },
    "rate-4": {
      type: "Button",
      props: { label: "4 \u2b50", variant: "outline", size: "sm" },
      on: {
        press: {
          action: "auto_send",
          params: { text: "I rate this conversation 4 out of 5 stars." },
        },
      },
    },
    "rate-5": {
      type: "Button",
      props: { label: "5 \u2b50", variant: "outline", size: "sm" },
      on: {
        press: {
          action: "auto_send",
          params: { text: "I rate this conversation 5 out of 5 stars!" },
        },
      },
    },
    sep: { type: "Separator", props: {} },
    "feedback-input": {
      type: "TextInput",
      props: {
        placeholder: "Any thoughts or suggestions? (optional)",
        submitLabel: "\ud83d\udcdd Send feedback",
      },
    },
  },
};

// --- 8. Trivia Quiz ---
const trivia: InteractiveSpec = {
  root: "trivia-card",
  elements: {
    "trivia-card": {
      type: "Card",
      props: { title: "Quick Trivia \ud83c\udfaf", description: null },
      children: ["trivia-emoji", "trivia-question", "trivia-options"],
    },
    "trivia-emoji": {
      type: "Emoji",
      props: { emoji: "\ud83e\udde0", size: "lg" },
    },
    "trivia-question": {
      type: "Text",
      props: { content: "How many bones does an adult human body have?" },
    },
    "trivia-options": {
      type: "ButtonGroup",
      props: { direction: "vertical" },
      children: ["opt-0", "opt-1", "opt-2"],
    },
    "opt-0": {
      type: "Button",
      props: { label: "A) 186", variant: "outline", size: "sm" },
      on: {
        press: { action: "auto_send", params: { text: "I think 186 bones!" } },
      },
    },
    "opt-1": {
      type: "Button",
      props: { label: "B) 206", variant: "outline", size: "sm" },
      on: {
        press: { action: "auto_send", params: { text: "I think 206 bones!" } },
      },
    },
    "opt-2": {
      type: "Button",
      props: { label: "C) 226", variant: "outline", size: "sm" },
      on: {
        press: { action: "auto_send", params: { text: "I think 226 bones!" } },
      },
    },
  },
};

// --- 9. Goal Planner ---
const goalPlanner: InteractiveSpec = {
  root: "goal-card",
  elements: {
    "goal-card": {
      type: "Card",
      props: {
        title: "\ud83c\udfaf Set a Goal for Today",
        description:
          "Pick a category and we\u2019ll build a mini plan together.",
      },
      children: ["goal-progress", "sep", "goal-categories"],
    },
    "goal-progress": {
      type: "ProgressBar",
      props: { value: 0, max: 3, label: "Daily goals" },
    },
    sep: { type: "Separator", props: {} },
    "goal-categories": {
      type: "Columns",
      props: { columns: 2 },
      children: ["btn-school", "btn-health", "btn-social", "btn-creative"],
    },
    "btn-school": {
      type: "Button",
      props: { label: "\ud83d\udcda School", variant: "outline", size: "sm" },
      on: {
        press: {
          action: "auto_send",
          params: { text: "I'd like to set a school/study goal for today." },
        },
      },
    },
    "btn-health": {
      type: "Button",
      props: { label: "\ud83d\udcaa Health", variant: "outline", size: "sm" },
      on: {
        press: {
          action: "auto_send",
          params: {
            text: "I'd like to set a health or fitness goal for today.",
          },
        },
      },
    },
    "btn-social": {
      type: "Button",
      props: { label: "\ud83d\udc9b Social", variant: "outline", size: "sm" },
      on: {
        press: {
          action: "auto_send",
          params: {
            text: "I'd like to set a social or relationship goal for today.",
          },
        },
      },
    },
    "btn-creative": {
      type: "Button",
      props: { label: "\ud83c\udfa8 Creative", variant: "outline", size: "sm" },
      on: {
        press: {
          action: "auto_send",
          params: { text: "I'd like to set a creative goal for today." },
        },
      },
    },
  },
};

// --- 10. Gratitude Prompt ---
const gratitude: InteractiveSpec = {
  root: "gratitude-card",
  elements: {
    "gratitude-card": {
      type: "Card",
      props: { title: "\ud83d\ude4f Gratitude Moment", description: null },
      children: [
        "gratitude-emoji",
        "gratitude-prompt",
        "sep",
        "gratitude-buttons",
      ],
    },
    "gratitude-emoji": {
      type: "Emoji",
      props: { emoji: "\u2728", size: "md" },
    },
    "gratitude-prompt": {
      type: "Text",
      props: {
        content:
          "Taking a moment to notice something good \u2014 even something small \u2014 can shift your whole mood. What's one thing you're grateful for today?",
      },
    },
    sep: { type: "Separator", props: {} },
    "gratitude-buttons": {
      type: "ButtonGroup",
      props: { direction: "horizontal" },
      children: ["btn-person", "btn-moment", "btn-thing", "btn-self"],
    },
    "btn-person": {
      type: "Button",
      props: { label: "\ud83e\udec2 A person", variant: "outline", size: "sm" },
      on: {
        press: {
          action: "auto_send",
          params: { text: "I'm grateful for someone in my life today." },
        },
      },
    },
    "btn-moment": {
      type: "Button",
      props: { label: "\ud83c\udf05 A moment", variant: "outline", size: "sm" },
      on: {
        press: {
          action: "auto_send",
          params: { text: "I'm grateful for a nice moment I had today." },
        },
      },
    },
    "btn-thing": {
      type: "Button",
      props: {
        label: "\ud83c\udf1f Something small",
        variant: "outline",
        size: "sm",
      },
      on: {
        press: {
          action: "auto_send",
          params: { text: "I'm grateful for something small but good today." },
        },
      },
    },
    "btn-self": {
      type: "Button",
      props: { label: "\ud83d\udcaa Myself", variant: "outline", size: "sm" },
      on: {
        press: {
          action: "auto_send",
          params: { text: "I'm grateful for something about myself today." },
        },
      },
    },
  },
};

const allSpecs = [
  { name: "1. Mood Check-in", marker: "mood-check", spec: moodCheck },
  {
    name: "2. Coping Strategy Picker",
    marker: "coping-toolkit",
    spec: copingToolkit,
  },
  { name: "3. Breathing Exercise", marker: "breathing", spec: breathing },
  { name: "4. Ice Breaker", marker: "ice-breaker", spec: iceBreaker },
  { name: "5. Session Check-in", marker: "check-in", spec: checkIn },
  { name: "6. Motivational Boost", marker: "motivation", spec: motivation },
  { name: "7. Session Feedback", marker: "feedback", spec: feedback },
  { name: "8. Trivia Quiz", marker: "trivia", spec: trivia },
  { name: "9. Goal Planner", marker: "goal-planner", spec: goalPlanner },
  { name: "10. Gratitude Prompt", marker: "gratitude", spec: gratitude },
];

function InteractiveDemoPage() {
  const [lastAction, setLastAction] = useState<string | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.type === "auto_send") {
        setLastAction(`auto_send: "${detail.text}"`);
      } else if (detail?.type === "send_message") {
        setLastAction(`send_message: "${detail.text}"`);
      } else if (detail?.type === "open_link") {
        setLastAction(`open_link: ${detail.url}`);
      }
    };
    window.addEventListener("interactive-action", handler);
    return () => window.removeEventListener("interactive-action", handler);
  }, []);

  return (
    <div className="flex-1 overflow-y-auto bg-background p-8">
      <div className="max-w-2xl mx-auto space-y-8">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            Interactive Engagement Demo
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            10 engagement experiences for students aged 12-18, rendered inside
            chat bubbles.
          </p>
        </div>

        {lastAction && (
          <div className="sticky top-4 z-10 bg-secondary border border-border rounded-lg p-3 text-sm text-foreground shadow-md">
            Last action: <code className="font-mono">{lastAction}</code>
          </div>
        )}

        <div className="space-y-6">
          <Message className="justify-end">
            <div className="max-w-[85%]">
              <MessageContent className="bg-primary text-primary-foreground">
                Hi, I could use some help today
              </MessageContent>
            </div>
          </Message>

          {allSpecs.map(({ name, marker, spec }) => (
            <div key={name} className="space-y-1.5">
              <div className="flex items-center gap-2 pl-1">
                <p className="text-xs font-semibold text-foreground">{name}</p>
                <code className="text-[10px] text-muted-foreground bg-secondary px-1.5 py-0.5 rounded">
                  [INTERACTIVE:{marker}]
                </code>
              </div>
              <Message>
                <div className="max-w-sm">
                  <div className="rounded-lg p-2 text-foreground bg-secondary border border-border/50 wrap-break-word whitespace-normal">
                    <InteractiveMessage spec={spec} />
                  </div>
                </div>
              </Message>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
